import { useRef } from "react";
import { useFrame, useThree } from "@react-three/fiber";
import * as THREE from "three";
import { ALL_BODY_IDS, type BodyId } from "@solar/ephemeris";
import { useScene } from "../store";
import { bodyName } from "../i18n";
import { bodyLive } from "./bodyState";
import { frame } from "./frame";

/** DOM 覆盖层：标签、北极 “N”、选中环。位置每帧由投影命令式更新，不触发 React 重渲染。 */
/** Canvas 外的 DOM 节点注册表；Canvas 内的 <OverlayDriver> 每帧读取并更新位置。 */
const reg = { tags: new Map<BodyId, HTMLDivElement>(), ns: new Map<BodyId, HTMLDivElement>(), marks: new Map<BodyId, HTMLDivElement>(), ring: null as HTMLDivElement | null };
const rel = new THREE.Vector3(), tip = new THREE.Vector3();

/** 放在 <Canvas> 内：只负责每帧投影与定位。位置一律用“相对浮动原点”的坐标，与 GPU 看到的一致。 */
export function OverlayDriver() {
  const v = useRef(new THREE.Vector3());
  const { camera, size } = useThree();
  useFrame(() => {
    const s = useScene.getState();
    const proj = (p: THREE.Vector3) => { v.current.copy(p).project(camera); return { x: (v.current.x * 0.5 + 0.5) * size.width, y: (-v.current.y * 0.5 + 0.5) * size.height, z: v.current.z }; };
    for (const id of ALL_BODY_IDS) {
      const el = reg.tags.get(id), nEl = reg.ns.get(id), mark = reg.marks.get(id), live = bodyLive.get(id);
      if (!el || !nEl || !mark) continue;
      const hideAll = () => { el.style.display = "none"; nEl.style.display = "none"; mark.style.display = "none"; };
      const show = !!live && live.visible && !s.hidden.has(id) && !(id === "moon" && s.hidden.has("earth"));
      if (!live || !show) { hideAll(); continue; }
      rel.copy(live.world).sub(frame.origin);
      const a = proj(rel);
      if (a.z > 1 || a.z < -1) { hideAll(); continue; }
      const rad = projectedRadius(camera, rel, live.radius, size.height);
      // 亚像素天体（真实比例全景）：画标记环，可点击；文字标签后移避开标记
      const tiny = rad < 4 && frame.m > 0.25;
      mark.style.display = tiny ? "" : "none";
      mark.style.opacity = String(Math.min(1, (frame.m - 0.25) / 0.4));
      if (tiny) mark.style.transform = `translate(${a.x - 8}px,${a.y - 8}px)`;
      el.style.display = s.layers.label ? "" : "none";
      el.style.transform = `translate(${a.x + (tiny ? 12 : rad + 6)}px,${a.y - 7}px)`;
      el.classList.toggle("sel", s.selected === id);
      if (s.layers.axis && rad > 4) {
        tip.copy(rel).addScaledVector(live.north, live.radius * (id === "saturn" ? 2.7 : 1.9));
        const b = proj(tip); nEl.style.display = ""; nEl.style.transform = `translate(${b.x + 3}px,${b.y - 12}px)`;
      } else nEl.style.display = "none";
      if (s.selected === id && reg.ring) {
        const r = Math.max(rad * 1.5, 14) + (id === "saturn" ? rad * 1.1 : 0);
        reg.ring.style.display = ""; reg.ring.style.width = reg.ring.style.height = `${r * 2}px`;
        reg.ring.style.transform = `translate(${a.x - r}px,${a.y - r}px)`;
      }
    }
    if (reg.ring && (!s.selected || !bodyLive.get(s.selected)?.visible)) reg.ring.style.display = "none";
  });
  return null;
}

/** 放在 <Canvas> 外：渲染标签 DOM。 */
export function OverlayView() {
  const lang = useScene((s) => s.lang);
  const tags = reg.tags, ns = reg.ns;
  return (
    <div className="overlay">
      {ALL_BODY_IDS.map((id) => (
        <div key={id}>
          <div className="tag" ref={(e) => { if (e) tags.set(id, e); else tags.delete(id); }} style={{ display: "none" }}>{bodyName(id, lang)}</div>
          <div className="marker" onClick={() => useScene.getState().select(id)} ref={(e) => { if (e) reg.marks.set(id, e); else reg.marks.delete(id); }} style={{ display: "none" }} />
          <div className="nlabel" ref={(e) => { if (e) ns.set(id, e); else ns.delete(id); }} style={{ display: "none" }}>N</div>
        </div>
      ))}
      <div className="ring" ref={(e) => { reg.ring = e; }} style={{ display: "none" }} />
    </div>
  );
}

/** 球体投影到屏幕的半径（px）。 */
function projectedRadius(cam: THREE.Camera, pos: THREE.Vector3, r: number, h: number): number {
  const d = cam.position.distanceTo(pos);
  const fov = ((cam as THREE.PerspectiveCamera).fov * Math.PI) / 180;
  return (r / (d * Math.tan(fov / 2))) * (h / 2);
}
